import { LitElement, css, html, nothing } from "lit";

/** Why the game could not start: a renderer that failed to initialise, or anything else. */
export type BootFailureKind = "generic" | "renderer";

export interface BootFailureBindings {
  onReload: () => void;
}

/** The words of a failure no reload mends (decision 81's refused launch), given by the boot in the player's language. */
export interface BootFinalCopy {
  title: string;
  message: string;
}

/**
 * The copy is built in, English only: the popup exists for the moment before any dictionary has
 * loaded. Failures after the chrome is up go to the system modal, which reads the dictionary.
 */
const COPY: Record<BootFailureKind, { title: string; message: string }> = {
  generic: {
    title: "Something went wrong",
    message: "The game could not start. Please reload the game.\nIf this keeps happening, contact support.",
  },
  renderer: {
    title: "This browser cannot run the game",
    message: "The game needs WebGL graphics, which this browser did not provide.\nTry reloading, or open the game in another browser.",
  },
};

const BUTTON_TEXT = "Reload";

/**
 * `<slot-boot-failure>`: the startup-failure popup. It depends on nothing the boot builds — not the
 * Pixi app, the chrome root, its theme or a dictionary — so it can show when any of those failed.
 * Mounted on the host element above the canvas and the overlay root; one button, which reloads —
 * or none, for a failure a reload cannot mend (`showFinal`).
 */
export class BootFailureElement extends LitElement {
  public static override properties = {
    open: { type: Boolean, reflect: true },
    kind: { type: String },
    finalCopy: { attribute: false },
  };

  public declare open: boolean;
  public declare kind: BootFailureKind;
  public declare finalCopy: BootFinalCopy | null;

  private bindings: BootFailureBindings | null = null;

  public constructor() {
    super();
    this.open = false;
    this.kind = "generic";
    this.finalCopy = null;
  }

  public static override styles = css`
    :host { display: none; position: absolute; inset: 0; z-index: 10; font-family: Roboto, system-ui, sans-serif; }
    :host([open]) { display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.741); }
    .window { box-sizing: border-box; width: min(88vw, 480px); padding: 32px 24px; border-radius: 16px; background: #ffffff; color: #000000; text-align: center; }
    .title { font-size: 22px; font-weight: 700; line-height: 1.2; }
    .message { margin: 16px 0 24px; font-size: 16px; font-weight: 500; line-height: 1.4; white-space: pre-wrap; }
    button { width: 100%; height: 48px; border: 0; border-radius: 8px; padding: 0; background: #00aa5a; color: #ffffff; font: inherit; font-size: 18px; font-weight: 500; cursor: pointer; touch-action: manipulation; }
    button:hover { filter: brightness(0.83); }
  `;

  public bind(bindings: BootFailureBindings): void {
    this.bindings = bindings;
  }

  public show(kind: BootFailureKind): void {
    this.kind = kind;
    this.open = true;
  }

  /** A failure no reload mends: its own words, and no button (decision 81). */
  public showFinal(copy: BootFinalCopy): void {
    this.finalCopy = copy;
    this.open = true;
  }

  protected override render() {
    const copy = this.finalCopy ?? COPY[this.kind];
    return html`
      <div class="window" role="alertdialog" aria-modal="true" aria-labelledby="title">
        <div class="title" id="title" data-part="title">${copy.title}</div>
        <div class="message" data-part="message">${copy.message}</div>
        ${this.finalCopy ? nothing : html`<button type="button" data-part="reload" @click=${() => this.bindings?.onReload()}>${BUTTON_TEXT}</button>`}
      </div>
    `;
  }
}

export const BOOT_FAILURE_TAG = "slot-boot-failure";

/** Defines the element once (a module is evaluated once per page, but tests re-import). */
export function defineBootFailureElement(): void {
  if (!customElements.get(BOOT_FAILURE_TAG)) customElements.define(BOOT_FAILURE_TAG, BootFailureElement);
}
